import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Database, Plus, RefreshCw, Trash2, PlugZap, CheckCircle2, XCircle, Columns3, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const VENDORS = [
  { id: 'SAP_S4HANA',    label: 'SAP S/4HANA (OData)',    hint: 'Host + client + service path' },
  { id: 'SAP_ECC_RFC',   label: 'SAP ECC (RFC / BAPI)',   hint: 'Host + system + user + pwd' },
  { id: 'ORACLE_EPPM',   label: 'Oracle EPPM (REST)',     hint: 'Base URL + JWT bearer' },
  { id: 'PRIMAVERA_P6',  label: 'Primavera P6 (XER)',     hint: 'Upload XER on each sync' },
  { id: 'ORACLE_FUSION', label: 'Oracle Fusion (REST)',   hint: 'Instance URL + OAuth2' },
  { id: 'CUSTOM_REST',   label: 'Custom REST endpoint',   hint: 'URL + headers + JSON mapping' },
];

// Column-Mapping modal. Given a connector, this fetches source columns
// from `/preview` and lets planners map each CBS target field onto one
// of them. Required fields are labelled with a red asterisk and can't be
// left blank.
function ColumnMappingModal({ open, onOpenChange, connector, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sourceColumns, setSourceColumns] = useState([]);
  const [targetFields, setTargetFields] = useState([]);
  const [columnMap, setColumnMap] = useState({});

  useEffect(() => {
    if (!open || !connector?.id) return;
    setLoading(true);
    api.get(`/data-connectors/${connector.id}/preview`)
      .then(({ data }) => {
        setSourceColumns(data.sourceColumns || []);
        setTargetFields(data.targetFields || []);
        setColumnMap(data.columnMap || {});
      })
      .catch(() => toast.error('Could not load source columns'))
      .finally(() => setLoading(false));
  }, [open, connector?.id]);

  const setMap = (fieldKey, sourceCol) => {
    setColumnMap((prev) => {
      const next = { ...prev };
      if (!sourceCol || sourceCol === '__none__') delete next[fieldKey];
      else next[fieldKey] = sourceCol;
      return next;
    });
  };

  const missingRequired = targetFields.filter((f) => f.required && !columnMap[f.key]);
  const duplicateSources = (() => {
    const seen = {};
    const dups = new Set();
    Object.entries(columnMap).forEach(([, v]) => {
      if (!v) return;
      if (seen[v]) dups.add(v);
      seen[v] = true;
    });
    return dups;
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/data-connectors/${connector.id}/column-map`, { columnMap });
      toast.success('Column mapping saved');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="column-map-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Columns3 className="w-5 h-5" /> Column Mapping — {connector?.name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Loading source columns…</div>
        ) : (
          <>
            <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
              Map each platform CBS field on the left to an ERP source column on the right. Required
              fields must be mapped before the connector will sync.
            </p>
            <div className="max-h-[420px] overflow-auto rounded border" style={{ borderColor: 'var(--border-color)' }}>
              <table className="w-full text-xs">
                <thead style={{ backgroundColor: 'var(--card-bg-alt)' }}>
                  <tr>
                    <th className="text-left p-2 w-1/2">Platform CBS Field</th>
                    <th className="text-left p-2 w-1/2">Source Column ({connector?.vendor})</th>
                  </tr>
                </thead>
                <tbody>
                  {targetFields.map((f) => {
                    const val = columnMap[f.key] || '';
                    const dup = val && duplicateSources.has(val);
                    return (
                      <tr key={f.key} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td className="p-2">
                          <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {f.label}
                            {f.required && <span className="text-red-500 ml-1">*</span>}
                          </div>
                          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{f.key}</div>
                        </td>
                        <td className="p-2">
                          <Select value={val || '__none__'} onValueChange={(v) => setMap(f.key, v)}>
                            <SelectTrigger data-testid={`map-${f.key}`} className={dup ? 'border-red-500' : ''}>
                              <SelectValue placeholder="— unmapped —" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— unmapped —</SelectItem>
                              {sourceColumns.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {dup && (
                            <div className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Column used in another mapping
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {missingRequired.length > 0 && (
              <div className="mt-2 text-[11px] text-amber-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Required fields still unmapped: {missingRequired.map((f) => f.label).join(', ')}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={saving || missingRequired.length > 0 || duplicateSources.size > 0}
                data-testid="save-column-map-btn"
              >
                {saving ? 'Saving…' : 'Save mapping'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function DataConnectorsAdmin() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [vendor, setVendor] = useState('SAP_S4HANA');
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [user, setUser] = useState('');
  const [pwd, setPwd] = useState('');
  const [cadence, setCadence] = useState('manual');
  const [busy, setBusy] = useState(false);
  const [mappingFor, setMappingFor] = useState(null); // connector currently being mapped

  const load = () => api.get('/data-connectors').then(r => setRows(r.data.connectors || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setBusy(true);
    try {
      await api.post('/data-connectors', {
        vendor, name, cadence,
        config: { host, user, password: pwd },
      });
      toast.success('Connector created');
      setCreating(false); setName(''); setHost(''); setUser(''); setPwd('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };

  const handleTest = async (id) => {
    try {
      const { data } = await api.post(`/data-connectors/${id}/test`);
      toast.success(data.message || 'Connection ok');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); }
  };
  const handleSync = async (id) => {
    try {
      const { data } = await api.post(`/data-connectors/${id}/sync`);
      toast.success(data.message || 'Sync started');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this connector?')) return;
    try { await api.delete(`/data-connectors/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4" data-testid="data-connectors-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Database className="w-6 h-6" /> Data Connectors
            </h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Import CBS, actuals, and progress from SAP, Oracle, Primavera P6 or any custom REST source.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="add-connector-btn">
            <Plus className="w-4 h-4 mr-1.5" />Add Connector
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.length === 0 && (
            <div className="col-span-full text-center text-sm py-12" style={{ color: 'var(--text-muted)' }}>
              No connectors yet. Click "Add Connector" to plug in SAP, Oracle or Primavera P6.
            </div>
          )}
          {rows.map(c => {
            const mapCount = c.columnMap ? Object.keys(c.columnMap).length : 0;
            return (
              <Card key={c.id} className="p-4" data-testid={`connector-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{c.name}</div>
                    <Badge variant="outline" className="text-[10px] mt-1">{c.vendor}</Badge>
                  </div>
                  <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:text-red-600" data-testid={`delete-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Cadence: <b>{c.cadence}</b> · Last run: {c.lastRunAt ? new Date(c.lastRunAt).toLocaleString() : 'never'}
                  {' '}{c.lastRunOk === true && <CheckCircle2 className="w-3 h-3 inline text-green-500" />}
                  {c.lastRunOk === false && <XCircle className="w-3 h-3 inline text-red-500" />}
                </div>
                <div className="text-[11px] mt-1" style={{ color: mapCount ? 'var(--text-muted)' : '#F59E0B' }}>
                  {mapCount ? `${mapCount} columns mapped` : 'No column mapping — sync will not run'}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => setMappingFor(c)} data-testid={`map-${c.id}`}>
                    <Columns3 className="w-3.5 h-3.5 mr-1" /> Map columns
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleTest(c.id)} data-testid={`test-${c.id}`}>
                    <PlugZap className="w-3.5 h-3.5 mr-1" /> Test
                  </Button>
                  <Button size="sm" onClick={() => handleSync(c.id)} data-testid={`sync-${c.id}`}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync now
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogContent className="max-w-md" data-testid="new-connector-dialog">
            <DialogHeader><DialogTitle>New Data Connector</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase font-semibold tracking-wider" style={{ color: 'var(--text-muted)' }}>Vendor</label>
                <Select value={vendor} onValueChange={setVendor}>
                  <SelectTrigger data-testid="vendor-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VENDORS.map(v => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {VENDORS.find(v => v.id === vendor)?.hint}
                </p>
              </div>
              <Input placeholder="Connector name" value={name} onChange={e => setName(e.target.value)} data-testid="name-input" />
              <Input placeholder="Host / Base URL" value={host} onChange={e => setHost(e.target.value)} data-testid="host-input" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="User / Client ID" value={user} onChange={e => setUser(e.target.value)} data-testid="user-input" />
                <Input placeholder="Password / Secret" type="password" value={pwd} onChange={e => setPwd(e.target.value)} data-testid="pwd-input" />
              </div>
              <Select value={cadence} onValueChange={setCadence}>
                <SelectTrigger data-testid="cadence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="hourly">Every hour</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleCreate} disabled={busy || !name} className="w-full" data-testid="create-connector-btn">
                {busy ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <ColumnMappingModal
          open={!!mappingFor}
          onOpenChange={(v) => { if (!v) setMappingFor(null); }}
          connector={mappingFor}
          onSaved={load}
        />
      </div>
    </DashboardLayout>
  );
}
